/**
 * Backend machine error codes the frontend branches on. Mirror of the values
 * in `Backend/src/common/constants/error-codes.ts` - keep the strings
 * identical.
 */
export const API_ERROR_CODES = {
  EMAIL_ALREADY_REGISTERED: 'EMAIL_ALREADY_REGISTERED',
  // Signup against the address of a closed (deleted) account.
  EMAIL_UNAVAILABLE: 'EMAIL_UNAVAILABLE',
  // 400: signup from the Issue Report CTA with a personal, free or disposable email (FR-207, MYN-286).
  ISSUE_REPORT_COMPANY_EMAIL_REQUIRED: 'ISSUE_REPORT_COMPANY_EMAIL_REQUIRED',
  /** An Issue Report signup arrived without company name and website (FR-218). */
  SIGNUP_COMPANY_DETAILS_REQUIRED: 'SIGNUP_COMPANY_DETAILS_REQUIRED',
  OTP_NOT_FOUND: 'OTP_NOT_FOUND',
  OTP_EXPIRED: 'OTP_EXPIRED',
  OTP_INVALID: 'OTP_INVALID',
  OTP_LOCKED: 'OTP_LOCKED',
  // Authenticator-app 2FA (MYN-241)
  TOTP_CODE_INVALID: 'TOTP_CODE_INVALID',
  RECOVERY_CODE_INVALID: 'RECOVERY_CODE_INVALID',
  /** Setup after an abandoned authenticator re-scan needs a recovery code (FR-221). */
  RECOVERY_CODE_REQUIRED: 'RECOVERY_CODE_REQUIRED',
  MFA_SESSION_EXPIRED: 'MFA_SESSION_EXPIRED',
  TOTP_NOT_ENROLLED: 'TOTP_NOT_ENROLLED',
  /** 429: five wrong second-factor codes within 15 minutes. */
  TWO_FACTOR_LOCKED: 'TWO_FACTOR_LOCKED',
  /** 400: the chosen HQ city is not in the chosen HQ country. */
  HQ_CITY_COUNTRY_MISMATCH: 'HQ_CITY_COUNTRY_MISMATCH',
  RATE_LIMITED: 'RATE_LIMITED',
  EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
  // Invites (MYN-43/47)
  INVITE_NOT_FOUND: 'INVITE_NOT_FOUND',
  INVITE_EXPIRED: 'INVITE_EXPIRED',
  INVITE_REVOKED: 'INVITE_REVOKED',
  INVITE_ALREADY_ACCEPTED: 'INVITE_ALREADY_ACCEPTED',
  INVITE_DOMAIN_MISMATCH: 'INVITE_DOMAIN_MISMATCH',
  INVITE_EMAIL_MISMATCH: 'INVITE_EMAIL_MISMATCH',
  INVITE_REQUIRES_VERIFICATION: 'INVITE_REQUIRES_VERIFICATION',
  /** 400: a sector was sent on an invite for an entity type that takes none (MYN-202). */
  INVITE_SECTOR_NOT_ALLOWED: 'INVITE_SECTOR_NOT_ALLOWED',
  /** 400: the sector id is not in the taxonomy (invite sector, colleague contact; MYN-202). */
  SECTOR_NOT_FOUND: 'SECTOR_NOT_FOUND',
  /** 400: two colleague-contact rows name the same sector (MYN-202). */
  COLLEAGUE_CONTACT_DUPLICATE_SECTOR: 'COLLEAGUE_CONTACT_DUPLICATE_SECTOR',
  // Shared Startup Profile (MYN-41/54/57/58)
  SHARE_NOT_FOUND: 'SHARE_NOT_FOUND',
  SHARE_EXPIRED: 'SHARE_EXPIRED',
  SHARE_REVOKED: 'SHARE_REVOKED',
  SHARE_NOT_VERIFIED: 'SHARE_NOT_VERIFIED',
  SHARE_RECIPIENT_ALREADY_EXISTS: 'SHARE_RECIPIENT_ALREADY_EXISTS',
  SHARE_RECIPIENT_NOT_FOUND: 'SHARE_RECIPIENT_NOT_FOUND',
  SHARE_RECIPIENT_REVOKED: 'SHARE_RECIPIENT_REVOKED',
  // Notifications (MYN-55)
  NOTIFICATION_NOT_FOUND: 'NOTIFICATION_NOT_FOUND',
  // Admin Startups console (MYN-127/131). `ENTITY_DELETED` is NOT repeated
  // here - it already lives in `ENTITY_STATUS_ERROR_CODES.DELETED`, and the
  // Backend returns the same code from both surfaces.
  // No STARTUP_PROFILE_FIELD_NOT_EDITABLE: the admin profile edit that answered
  // with it was withdrawn on the sprint 3 feedback, in both repos.
  STARTUP_NOT_FOUND: 'STARTUP_NOT_FOUND',
  // Startups (MYN-46)
  STARTUP_DOMAIN_ALREADY_REGISTERED: 'STARTUP_DOMAIN_ALREADY_REGISTERED',
  JOIN_REQUEST_COOLDOWN: 'JOIN_REQUEST_COOLDOWN',
  // Events (MYN-51)
  EVENT_NOT_FOUND: 'EVENT_NOT_FOUND',
  // Public (account-less) event registration (FR-282, FR-284). Both 400, the same
  // refusals a member gets; a 429 carries no code and is matched on status.
  /** 400: the event is not published, or is cancelled or archived. */
  EVENT_NOT_REGISTERABLE: 'EVENT_NOT_REGISTERABLE',
  /** 400: registration for the event has closed (FR-197). */
  EVENT_REGISTRATION_CLOSED: 'EVENT_REGISTRATION_CLOSED',
  // `checkEligibility`/`createStartup` reject a personal/social account email
  // with this before ever checking STARTUP_DOMAIN_ALREADY_REGISTERED above.
  // Also gates FR-14's guided email-replacement flow (MYN-76).
  WORK_EMAIL_REQUIRED: 'WORK_EMAIL_REQUIRED',
  // Individual account password/delete
  PASSWORD_NOT_SET: 'PASSWORD_NOT_SET',
  CURRENT_PASSWORD_INCORRECT: 'CURRENT_PASSWORD_INCORRECT',
  PASSWORD_CONFIRMATION_MISMATCH: 'PASSWORD_CONFIRMATION_MISMATCH',
  NEW_PASSWORD_SAME_AS_CURRENT: 'NEW_PASSWORD_SAME_AS_CURRENT',
  ACCOUNT_OWNS_ACTIVE_ENTITY: 'ACCOUNT_OWNS_ACTIVE_ENTITY',
  // Email replacement (FR-14/FR-68, MYN-76). The collision case reuses
  // EMAIL_ALREADY_REGISTERED above rather than a new code.
  EMAIL_REPLACEMENT_NOT_COMPANY_EMAIL: 'EMAIL_REPLACEMENT_NOT_COMPANY_EMAIL',
  EMAIL_REPLACEMENT_REQUEST_NOT_FOUND: 'EMAIL_REPLACEMENT_REQUEST_NOT_FOUND',
  EMAIL_REPLACEMENT_REQUEST_EXPIRED: 'EMAIL_REPLACEMENT_REQUEST_EXPIRED',
  // 409 once the account's one self-service replacement is spent (UX sheet r74).
  EMAIL_REPLACEMENT_ALREADY_USED: 'EMAIL_REPLACEMENT_ALREADY_USED',
  // Native matching engine (MYN-168 to MYN-174, M10).
  MATCHING_RUN_ALREADY_IN_PROGRESS: 'MATCHING_RUN_ALREADY_IN_PROGRESS',
  MATCHING_RUN_NO_ELIGIBLE_CANDIDATES: 'MATCHING_RUN_NO_ELIGIBLE_CANDIDATES',
  MATCHING_RUN_NOT_FOUND: 'MATCHING_RUN_NOT_FOUND',
  // Admin communications (MYN-136, Card 18).
  /** 409: the audience no longer matches the counts the admin confirmed. */
  AUDIENCE_CHANGED: 'AUDIENCE_CHANGED',
  /** 413: over `ADMIN_COMMUNICATION_MAX_RECIPIENTS`; refused before any rows load. */
  COMMUNICATION_AUDIENCE_TOO_LARGE: 'COMMUNICATION_AUDIENCE_TOO_LARGE',
  /** 422: nobody in the audience is eligible. */
  COMMUNICATION_NO_RECIPIENTS: 'COMMUNICATION_NO_RECIPIENTS',
  /** 503: queueing failed after the row committed; the Backend rolled it back, so a retry is genuine. */
  COMMUNICATION_QUEUE_FAILED: 'COMMUNICATION_QUEUE_FAILED',
  // Plan configuration (FR-40, FR-199, FR-200).
  /** 400: the plan's interval, term or service pairing is inconsistent (e.g. an Issue Report plan that is not one-time). */
  PLAN_CONFIGURATION_INVALID: 'PLAN_CONFIGURATION_INVALID',
  /** 409: another active plan is already the Issue Report plan (FR-200). */
  ISSUE_REPORT_PLAN_ALREADY_ACTIVE: 'ISSUE_REPORT_PLAN_ALREADY_ACTIVE',
  // Resource folders (MYN-282).
  /** 409: the submitted folder order no longer matches the folders that exist. */
  RESOURCE_FOLDER_ORDER_STALE: 'RESOURCE_FOLDER_ORDER_STALE',
  /** 400: a resource was saved with no folder (create, or an update that empties it). */
  RESOURCE_FOLDER_REQUIRED: 'RESOURCE_FOLDER_REQUIRED',
  /** 400: a resource was filed or moved into a retired folder. */
  RESOURCE_FOLDER_RETIRED: 'RESOURCE_FOLDER_RETIRED',
  // View as user (MYN-332/333).
  /** 403: the session ended, expired, or its admin lost the permission; the tab cannot go on. */
  VIEW_AS_SESSION_ENDED: 'VIEW_AS_SESSION_ENDED',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];
